import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";

import { CONTENT_TUTORIAL_SCENES } from "./contentTutorialMocks.js";

/**
 * useTutorial — guided tour of the content generator over a mock client.
 *
 * One controlled Joyride: each step belongs to a scene (route + mock data the pages render).
 * Siguiente/Anterior move between steps; changing scene navigates, opens or closes the create
 * modal and swaps the mock. Nothing is created or fetched.
 */

export const CONTENT_GENERATOR_TUTORIAL_INTRO = {
  title: "Antes de empezar",
  description:
    "Este tutorial recorre el flujo completo del generador de contenidos: desde la lista de contenidos hasta dejar una pieza revisada por el cliente y programada.",
  badge: "Ejemplo guiado",
  highlightTitle: "Vas a recorrer un ejemplo con un cliente ficticio",
  highlightDescription:
    "No se crea ni se envía nada real. Usaremos un cliente de ejemplo y en cada paso verás ya rellenado lo que tendrías que elegir o escribir.",
  bulletTitle: "Durante el tutorial",
  bullets: [
    "Conocerás la lista de contenidos, las próximas publicaciones y la vista de calendario.",
    "Verás cómo se crea un contenido con IA: cliente, título, texto base y redes.",
    "Recorrerás el contenido generado: adaptaciones a cada red, revisión con el cliente y programación.",
  ],
  note: "Avanza con Siguiente y vuelve atrás cuando quieras. Puedes salir en cualquier momento con Finalizar tutorial.",
  confirmLabel: "Empezar tutorial",
};

export const CONTENT_GENERATOR_TUTORIAL_COMPLETION_DESCRIPTION =
  "Has completado el tutorial del generador de contenidos. Ya sabes cómo crear un contenido con IA, revisar su texto base, adaptarlo a cada red, revisarlo con el cliente y programarlo.";

const step = (scene, target, title, content, extra = {}) => ({
  scene,
  target: `[data-tutorial="${target}"]`,
  title,
  content,
  placement: "auto",
  disableBeacon: true,
  ...extra,
});

const RAW_STEPS = [
  // ─── List ───
  step(
    "list",
    "content-table",
    "Tus contenidos",
    "Aquí tienes todos los contenidos de tus clientes con su estado. Usa las pestañas para filtrar por estado, el buscador y el botón de filtros para ver un único cliente. Al hacer clic en una fila se abre el contenido."
  ),
  step(
    "list",
    "upcoming-publications",
    "Próximas publicaciones",
    "Las publicaciones ya programadas aparecen aquí ordenadas por fecha. Al hacer clic en cualquiera se abre su contenido."
  ),
  step(
    "list",
    "btn-calendar",
    "Listado o calendario",
    "Cambia entre el listado y el calendario, donde ves cada publicación en su fecha y puedes programar las pendientes."
  ),
  step(
    "list",
    "btn-generate",
    "Crear contenido",
    <>
      Con <strong>Crear contenido</strong> eliges el cliente y el tipo de contenido que quieres
      generar. Pulsa Siguiente y lo abriremos con un cliente de ejemplo.
    </>
  ),

  // ─── Create modal ───
  step(
    "create_modal",
    "create-content-form",
    "Elige cliente y tipo",
    <>
      Seleccionas el <strong>cliente</strong> y el modo <strong>Con IA</strong> (o Manual si
      prefieres escribir tú cada red). Revisa sus <strong>buyer personas</strong>: la IA escribe
      para ellas. Después eliges <strong>Contenido individual</strong> para una sola pieza o{" "}
      <strong>Campaña</strong> para varias. En el ejemplo, un contenido individual.
    </>,
    {
      placement: "right",
      // The dialog paper, not the form: Joyride misplaces the spotlight of a target inside the
      // dialog's scrolling body (it uses offsetTop relative to the paper).
      target: '.MuiDialog-paper:has([data-tutorial="create-content-form"])',
    }
  ),

  // ─── Creation page: single AI piece ───
  step(
    "single_briefing",
    "cpm-input-form",
    "Título y tono",
    <>
      Escribe el <strong>título o tema</strong> del contenido (no es el prompt: a partir de él la
      IA redacta el texto base) y ajusta tono, lenguaje e idioma, que vienen de la configuración
      del cliente. Al pulsar <strong>Generar contenido</strong> la IA se pone a escribir.
    </>,
    { placement: "right" }
  ),
  step(
    "single_base",
    "cpm-raw-editor",
    "Texto base generado",
    "Este es el texto base que ha redactado la IA, del que saldrán las adaptaciones a cada red. Ya está guardado como borrador: revísalo y edítalo directamente aquí si quieres."
  ),
  step(
    "single_base",
    "cpm-raw-editor",
    "Elige las redes",
    <>
      Marca las <strong>redes</strong> en las que se publicará (en el ejemplo, Instagram,
      Facebook y LinkedIn) y pulsa <strong>Generar adaptaciones</strong>: la IA crea una versión
      para cada una y se abre el contenido.
    </>
  ),

  // ─── Piece page: adaptations ───
  step(
    "piece_complete",
    "cpm-workflow-stepper",
    "Las fases del contenido",
    <>
      Cada contenido avanza por tres fases: <strong>Generación</strong>,{" "}
      <strong>Revisión del cliente</strong> y <strong>Programación</strong>. Cada fase se
      desbloquea al completar la anterior y puedes volver a las ya abiertas haciendo clic.
    </>,
    { placement: "bottom" }
  ),
  step(
    "piece_complete",
    "cpm-network-tabs",
    "Una pestaña por red",
    "Cada red tiene su adaptación. Cambia de pestaña para editarlas: ajusta el texto, añade imágenes o vídeo, cambia el formato y comprueba cómo se verá la publicación."
  ),
  step(
    "piece_complete",
    "cpm-send-to-client",
    "Envíalo al cliente",
    <>
      Cuando esté listo, <strong>Enviar al cliente</strong> lo pasa a revisión: el cliente lo verá
      en su portal, podrá comentarlo y aprobarlo.
    </>
  ),

  // ─── Piece page: review and scheduling ───
  step(
    "piece_review",
    "cpm-review-comments",
    "Conversación con el cliente",
    <>
      Aquí ves los comentarios del cliente y le respondes. El <strong>historial de versiones</strong>{" "}
      te deja comparar los cambios.
    </>,
    { placement: "right" }
  ),
  step(
    "piece_scheduling",
    "cpm-scheduling",
    "Programa la publicación",
    <>
      Con el contenido aprobado, elige la fecha y pulsa <strong>Programar</strong>: se publicará a
      la vez en todas las redes conectadas.
    </>,
    { placement: "right" }
  ),

  // ─── Back on the list ───
  step(
    "list_group",
    "btn-group-content",
    "Agrupar contenidos",
    "Con un cliente seleccionado puedes agrupar contenidos que aún no se han enviado para presentarlos juntos: enviar uno envía todos y, si el cliente aprueba uno, se aprueban todos. Es ideal para enseñar una campaña completa de una vez."
  ),
];

export const CONTENT_TUTORIAL_STEPS = RAW_STEPS.map((item, index) => ({
  ...item,
  globalStep: index + 1,
  globalTotal: RAW_STEPS.length,
}));

const TARGET_WAIT_MS = 4000;

const currentPath = (location) => `${location.pathname}${location.search}`;

export function useTutorial() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [joyrideRun, setJoyrideRun] = useState(false);
  const [joyrideKey, setJoyrideKey] = useState(0);
  const [showTutorialSuccess, setShowTutorialSuccess] = useState(false);
  const pendingFrameRef = useRef(null);
  const pendingTimeoutRef = useRef(null);
  const locationRef = useRef(location);
  locationRef.current = location;

  const clearPending = useCallback(() => {
    if (pendingFrameRef.current !== null) {
      cancelAnimationFrame(pendingFrameRef.current);
      pendingFrameRef.current = null;
    }
    if (pendingTimeoutRef.current !== null) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearPending, [clearPending]);

  /** Shows step `index`: same scene → Joyride just moves on; another scene → swap and relaunch. */
  const goToStep = useCallback(
    (index, fromIndex = null) => {
      const target = CONTENT_TUTORIAL_STEPS[index];
      if (!target) return;
      const from = fromIndex === null ? null : CONTENT_TUTORIAL_STEPS[fromIndex];
      clearPending();

      if (from && from.scene === target.scene) {
        setStepIndex(index);
        return;
      }

      setJoyrideRun(false);
      setStepIndex(index);
      const scene = CONTENT_TUTORIAL_SCENES[target.scene];
      if (scene.path && currentPath(locationRef.current) !== scene.path) {
        navigate(scene.path, { replace: true });
      }

      const startedAt = Date.now();
      const tryLaunch = () => {
        if (document.querySelector(target.target) || Date.now() - startedAt > TARGET_WAIT_MS) {
          pendingFrameRef.current = null;
          setJoyrideKey((key) => key + 1);
          setJoyrideRun(true);
          return;
        }
        pendingFrameRef.current = requestAnimationFrame(tryLaunch);
      };
      pendingTimeoutRef.current = setTimeout(() => {
        pendingTimeoutRef.current = null;
        pendingFrameRef.current = requestAnimationFrame(tryLaunch);
      }, scene.delay || 0);
    },
    [clearPending, navigate]
  );

  const resetState = useCallback(() => {
    clearPending();
    setIsTutorialActive(false);
    setJoyrideRun(false);
    setStepIndex(0);
    // The mock routes (campaign, piece) have no real data behind them.
    const listPath = CONTENT_TUTORIAL_SCENES.list.path;
    if (locationRef.current.pathname !== listPath) navigate(listPath, { replace: true });
  }, [clearPending, navigate]);

  const startTutorial = useCallback(() => {
    setShowTutorialSuccess(false);
    setIsTutorialActive(true);
    goToStep(0);
  }, [goToStep]);

  const stopTutorial = useCallback(() => resetState(), [resetState]);

  const finishTutorial = useCallback(() => {
    resetState();
    setShowTutorialSuccess(true);
  }, [resetState]);

  const handleJoyrideCallback = useCallback(
    ({ action, index, status, type }) => {
      if (status === STATUS.SKIPPED || action === ACTIONS.SKIP) {
        stopTutorial();
        return;
      }
      if (type !== EVENTS.STEP_AFTER && type !== EVENTS.TARGET_NOT_FOUND) return;

      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      if (nextIndex >= CONTENT_TUTORIAL_STEPS.length) {
        finishTutorial();
        return;
      }
      goToStep(Math.max(nextIndex, 0), index);
    },
    [finishTutorial, goToStep, stopTutorial]
  );

  const sceneKey = isTutorialActive ? CONTENT_TUTORIAL_STEPS[stepIndex]?.scene ?? null : null;
  const scene = sceneKey ? CONTENT_TUTORIAL_SCENES[sceneKey] : null;
  const tutorialMock = useMemo(() => (scene ? { scene: sceneKey, ...scene.mock } : null), [scene, sceneKey]);

  return {
    isTutorialActive,
    tutorialMock,
    tutorialScene: scene,
    steps: CONTENT_TUTORIAL_STEPS,
    stepIndex,
    joyrideRun,
    joyrideKey,
    startTutorial,
    stopTutorial,
    handleJoyrideCallback,
    showTutorialSuccess,
    setShowTutorialSuccess,
  };
}
